import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  analyticsKpis,
  timeSeriesData,
  topListeners,
  topFollowers,
  topMessages,
  topGrowth,
  topLives,
  topStreamDown,
  topActive,
  topScore,
  additionalCharts,
  additionalTimeSeriesData,
  defaultFilters,
  type TimeRange,
  type AnalyticsFilters,
  type TopRadio,
} from '@/mocks/superAdminAnalytics';
import {
  growthMetrics,
  radioGrowthData,
  growthAlerts,
  type RadioGrowthData,
  type GrowthAlert,
  type GrowthAlertType,
} from '@/mocks/growthCenter';
import { executiveReports, type ExecutiveReport } from '@/mocks/superAdminExecutiveReports';
import { SwalAlert } from '@/hooks/useSweetAlert';
import KpiCard from './components/KpiCard';
import TimeSeriesChart from './components/TimeSeriesChart';
import TopRankingList from './components/TopRankingList';
import TimeRangeSelector from './components/TimeRangeSelector';
import AnalyticsFiltersComponent from './components/AnalyticsFilters';
import RadioDetailModal from './components/RadioDetailModal';

type LoadingState = 'idle' | 'loading' | 'error' | 'ready';
type TabKey = 'overview' | 'rankings' | 'growth-alerts' | 'reports';

const chartTitles: Record<TimeRange, string> = {
  diario: 'Oyentes — Últimas 24 Horas',
  semanal: 'Oyentes — Última Semana',
  mensual: 'Oyentes — Último Mes',
  anual: 'Oyentes — Último Año',
};

function getFilterMultiplier(filters: AnalyticsFilters): number {
  let m = 1;
  if (filters.plan === 'premium') m *= 1.35;
  if (filters.plan === 'pro') m *= 1.15;
  if (filters.plan === 'free') m *= 0.6;
  if (filters.category === 'musical') m *= 1.2;
  if (filters.category === 'cultural') m *= 0.7;
  if (filters.category === 'religiosa') m *= 0.4;
  if (filters.status === 'activa') m *= 1.05;
  if (filters.status === 'inactiva') m *= 0.25;
  if (filters.status === 'suspendida') m *= 0.1;
  if (filters.dateFrom || filters.dateTo) m *= 0.65;
  return Math.max(0.05, m);
}

function applyFilterToData<T extends TopRadio>(data: T[], filters: AnalyticsFilters): T[] {
  return data.filter((item) => {
    if (filters.plan !== 'todos' && item.plan !== filters.plan) return false;
    if (filters.category !== 'todas' && item.category !== filters.category) return false;
    if (filters.country !== 'todos' && item.country !== filters.country) return false;
    if (filters.status !== 'todas' && item.status !== filters.status) return false;
    return true;
  }).map((item, idx) => ({ ...item, rank: idx + 1 }));
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatPercent(n: number): string {
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildReportCsv(report: ExecutiveReport): string {
  const header = 'Métrica,Valor,Cambio\n';
  const rows = report.metrics.map((m) => `"${m.label}","${m.value}","${m.change}"`).join('\n');
  return header + rows;
}

/* ── Growth Alert Card ── */
function GrowthAlertCard({ alert, onResolve, onNote }: {
  alert: GrowthAlert;
  onResolve: (id: number) => void;
  onNote: (id: number) => void;
}) {
  const typeDef = {
    growing_fast: { label: 'Creciendo Rápido', icon: 'ri-rocket-2-line', bg: 'bg-emerald-50', color: 'text-emerald-500', badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-700', border: 'border-emerald-200' },
    at_risk: { label: 'En Riesgo', icon: 'ri-alert-line', bg: 'bg-amber-50', color: 'text-amber-500', badgeBg: 'bg-amber-100', badgeText: 'text-amber-700', border: 'border-amber-200' },
    no_activity: { label: 'Sin Actividad', icon: 'ri-pause-circle-line', bg: 'bg-red-50', color: 'text-red-400', badgeBg: 'bg-red-100', badgeText: 'text-red-700', border: 'border-red-200' },
  }[alert.type];

  return (
    <div className={`bg-background-50 border ${typeDef.border} rounded-2xl p-4 hover:border-background-300 transition-colors space-y-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-xl ${typeDef.bg} flex items-center justify-center shrink-0`}>
            <i className={`${typeDef.icon} ${typeDef.color} text-base`}></i>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-800 text-xs font-semibold line-clamp-1">{alert.radioName}</p>
            <p className="text-foreground-400 text-[9px]">{alert.radioFrequency} · {alert.radioCity}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full ${typeDef.badgeBg} ${typeDef.badgeText} text-[9px] font-medium shrink-0 whitespace-nowrap`}>
          <i className={`${typeDef.icon} text-[9px]`}></i>
          {typeDef.label}
        </span>
      </div>
      <p className="text-foreground-800 text-[11px] font-semibold leading-snug">{alert.message}</p>
      <p className="text-foreground-400 text-[10px] leading-relaxed line-clamp-3">{alert.detail}</p>
      <div className="flex items-center gap-4 pt-1 border-t border-background-100">
        <div className="flex items-center gap-1.5">
          <span className="text-foreground-400 text-[9px]">{alert.metric}</span>
          <span className={`text-[10px] font-bold ${alert.changePercent >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {formatPercent(alert.changePercent)}
          </span>
        </div>
        <div className="w-px h-4 bg-background-200"></div>
        <div className="flex items-center gap-1 text-foreground-400 text-[9px]">
          <i className="ri-time-line text-[9px]"></i>
          {alert.since}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <button onClick={() => onNote(alert.id)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Agregar nota">
            <i className="ri-sticky-note-line text-foreground-400 text-xs"></i>
          </button>
          <button onClick={() => onResolve(alert.id)} className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100 transition-colors" title="Marcar revisada">
            <i className="ri-check-line text-emerald-500 text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Report Card ── */
function ReportCard({ report, onEdit, onDuplicate, onDelete, onRun, onCsv, onPdf }: {
  report: ExecutiveReport;
  onEdit: (r: ExecutiveReport) => void;
  onDuplicate: (r: ExecutiveReport) => void;
  onDelete: (r: ExecutiveReport) => void;
  onRun: (r: ExecutiveReport) => void;
  onCsv: (r: ExecutiveReport) => void;
  onPdf: (r: ExecutiveReport) => void;
}) {
  const trendIcon = (trend: string) => {
    if (trend === 'up') return 'ri-arrow-up-line text-emerald-500';
    if (trend === 'down') return 'ri-arrow-down-line text-red-500';
    return 'ri-subtract-line text-foreground-400';
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-5 flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
            <i className={`${report.icon} text-foreground-600 text-lg`}></i>
          </div>
          <div>
            <h3 className="text-foreground-800 text-sm font-bold">{report.title}</h3>
            <p className="text-foreground-400 text-[10px]">{report.period}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onRun(report)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Ejecutar">
            <i className="ri-play-line text-foreground-500 text-xs"></i>
          </button>
          <button onClick={() => onEdit(report)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Editar">
            <i className="ri-pencil-line text-foreground-500 text-xs"></i>
          </button>
          <button onClick={() => onDuplicate(report)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Duplicar">
            <i className="ri-file-copy-line text-foreground-500 text-xs"></i>
          </button>
          <button onClick={() => onDelete(report)} className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors" title="Eliminar">
            <i className="ri-delete-bin-line text-red-400 text-xs"></i>
          </button>
        </div>
      </div>

      <p className="text-foreground-400 text-[11px] leading-relaxed mb-4">{report.description}</p>

      <div className="space-y-2 mb-5 flex-1">
        {report.metrics.slice(0, 5).map((metric, idx) => (
          <div key={idx} className="flex items-center justify-between px-3 py-2 rounded-xl bg-background-100/60">
            <span className="text-foreground-500 text-[11px] font-medium">{metric.label}</span>
            <div className="flex items-center gap-2">
              <span className="text-foreground-800 text-xs font-bold">{metric.value}</span>
              <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${metric.trend === 'up' ? 'text-emerald-600' : metric.trend === 'down' ? 'text-red-500' : 'text-foreground-400'}`}>
                <i className={`${trendIcon(metric.trend)} text-[9px]`}></i>
                {metric.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-background-200">
        <button onClick={() => onPdf(report)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-600 text-[11px] font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-file-pdf-line text-xs"></i>PDF
        </button>
        <button onClick={() => onCsv(report)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-600 text-[11px] font-semibold hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-file-excel-2-line text-xs"></i>CSV
        </button>
        <button onClick={() => onRun(report)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary-50 text-primary-600 text-[11px] font-semibold hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap ml-auto">
          <i className="ri-send-plane-line text-xs"></i>Ejecutar
        </button>
      </div>
    </div>
  );
}

/* ── Report Form Modal ── */
function ReportFormModal({ report, onClose, onSave }: {
  report: ExecutiveReport | null;
  onClose: () => void;
  onSave: (r: ExecutiveReport) => void;
}) {
  const isNew = !report;
  const [formData, setFormData] = useState({
    title: report?.title || '',
    description: report?.description || '',
    period: report?.period || '',
    icon: report?.icon || 'ri-bar-chart-2-line',
    reportType: 'audience' as string,
    frequency: 'manual' as string,
  });

  const handleSave = () => {
    const saved: ExecutiveReport = {
      id: report?.id || `rpt-${Date.now()}`,
      title: formData.title,
      description: formData.description,
      period: formData.period,
      icon: formData.icon,
      metrics: report?.metrics || [],
    };
    onSave(saved);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between gap-4 z-10">
          <h3 className="text-foreground-950 font-bold text-sm">{isNew ? 'Nuevo Reporte' : 'Editar Reporte'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-foreground-500 text-[10px] font-semibold mb-1">Nombre</label>
            <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors" placeholder="Nombre del reporte" />
          </div>
          <div>
            <label className="block text-foreground-500 text-[10px] font-semibold mb-1">Descripción</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors resize-none" placeholder="Descripción del reporte" />
          </div>
          <div>
            <label className="block text-foreground-500 text-[10px] font-semibold mb-1">Período</label>
            <input type="text" value={formData.period} onChange={(e) => setFormData({ ...formData, period: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors" placeholder="Ej: Julio 2026" />
          </div>
          <div>
            <label className="block text-foreground-500 text-[10px] font-semibold mb-1">Tipo de Reporte</label>
            <select value={formData.reportType} onChange={(e) => setFormData({ ...formData, reportType: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer">
              <option value="audience">Audiencia</option>
              <option value="engagement">Engagement</option>
              <option value="revenue">Revenue</option>
              <option value="growth">Crecimiento</option>
              <option value="content">Contenido</option>
              <option value="technical">Salud Técnica</option>
            </select>
          </div>
          <div>
            <label className="block text-foreground-500 text-[10px] font-semibold mb-1">Frecuencia Programada</label>
            <select value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer">
              <option value="manual">Manual</option>
              <option value="daily">Diario</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensual</option>
            </select>
          </div>
        </div>

        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
          <button onClick={handleSave} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap" disabled={!formData.title || !formData.period}>
            {isNew ? 'Crear Reporte' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */

export default function SuperAdminAnalytics() {
  const [loadState, setLoadState] = useState<LoadingState>('loading');
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [timeRange, setTimeRange] = useState<TimeRange>('semanal');
  const [filters, setFilters] = useState<AnalyticsFilters>(defaultFilters);
  const [selectedRadio, setSelectedRadio] = useState<TopRadio | null>(null);

  // Growth alerts state
  const [alerts, setAlerts] = useState<GrowthAlert[]>(growthAlerts);
  const [alertFilter, setAlertFilter] = useState<GrowthAlertType | 'all'>('all');

  // Reports state
  const [reports, setReports] = useState<ExecutiveReport[]>(executiveReports);
  const [reportForm, setReportForm] = useState<ExecutiveReport | null>(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportToast, setReportToast] = useState<string | null>(null);

  useEffect(() => {
    setLoadState('loading');
    const delay = 700 + Math.random() * 600;
    const shouldError = Math.random() < 0.08;
    const timer = setTimeout(() => setLoadState(shouldError ? 'error' : 'ready'), delay);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setLoadState('loading');
    const delay = 500 + Math.random() * 400;
    setTimeout(() => setLoadState(Math.random() < 0.05 ? 'error' : 'ready'), delay);
  }, []);

  const handleFilterChange = useCallback((newFilters: AnalyticsFilters) => {
    setFilters(newFilters);
    setLoadState('loading');
    setTimeout(() => setLoadState('ready'), 300 + Math.random() * 300);
  }, []);

  const handleClearFilters = useCallback(() => {
    handleFilterChange(defaultFilters);
  }, [handleFilterChange]);

  const hasActiveFilters = useMemo(() => {
    return filters.plan !== 'todos' || filters.category !== 'todas' || filters.country !== 'todos' || filters.status !== 'todas' || filters.dateFrom !== '' || filters.dateTo !== '';
  }, [filters]);

  const currentTimeSeries = useMemo(() => {
    const raw = timeSeriesData[timeRange];
    const multiplier = getFilterMultiplier(filters);
    return raw.map((point) => ({ ...point, value: Math.round(point.value * multiplier), previousValue: Math.round(point.previousValue * multiplier * 0.9) }));
  }, [timeRange, filters]);

  const filteredKpis = useMemo(() => {
    const multiplier = getFilterMultiplier(filters);
    return analyticsKpis.map((kpi) => ({ ...kpi, value: Math.round(kpi.value * multiplier), previousValue: Math.round(kpi.previousValue * multiplier * 0.85) }));
  }, [filters]);

  const filteredListeners = useMemo(() => applyFilterToData(topListeners, filters), [filters]);
  const filteredActive = useMemo(() => applyFilterToData(topActive, filters), [filters]);
  const filteredMessages = useMemo(() => applyFilterToData(topMessages, filters), [filters]);
  const filteredScore = useMemo(() => applyFilterToData(topScore, filters), [filters]);
  const filteredStreamDown = useMemo(() => applyFilterToData(topStreamDown, filters), [filters]);

  const allRankingsEmpty = useMemo(() => (
    filteredListeners.length === 0 && filteredActive.length === 0 && filteredMessages.length === 0 && filteredScore.length === 0 && filteredStreamDown.length === 0
  ), [filteredListeners, filteredActive, filteredMessages, filteredScore, filteredStreamDown]);

  const additionalChartData = useMemo(() => {
    const multiplier = getFilterMultiplier(filters);
    return additionalCharts.map((chart) => {
      const raw = additionalTimeSeriesData[chart.key]?.[timeRange] || [];
      return { ...chart, data: raw.map((point) => ({ ...point, value: Math.round(point.value * multiplier), previousValue: Math.round(point.previousValue * multiplier * 0.85) })) };
    });
  }, [timeRange, filters]);

  const filteredAlerts = useMemo(() => {
    if (alertFilter === 'all') return alerts;
    return alerts.filter((a) => a.type === alertFilter);
  }, [alerts, alertFilter]);

  // Alert actions
  const handleResolveAlert = useCallback(async (id: number) => {
    const result = await SwalAlert.confirm('Marcar como revisada', '¿Confirmás que esta alerta fue revisada?');
    if (result.isConfirmed) {
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      SwalAlert.success('Listo', 'Alerta marcada como revisada.');
    }
  }, []);

  const handleNoteAlert = useCallback(async (id: number) => {
    const { value: note } = await SwalAlert.fire({
      title: 'Agregar nota',
      input: 'textarea',
      inputPlaceholder: 'Escribí tu nota...',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      customClass: { popup: 'rounded-2xl', confirmButton: 'rounded-xl', cancelButton: 'rounded-xl' },
    });
    if (note) {
      SwalAlert.success('Nota guardada', 'La nota se agregó a la alerta.');
    }
  }, []);

  // Report actions
  const handleCreateReport = useCallback(() => {
    setReportForm(null);
    setShowReportForm(true);
  }, []);

  const handleEditReport = useCallback((r: ExecutiveReport) => {
    setReportForm(r);
    setShowReportForm(true);
  }, []);

  const handleDuplicateReport = useCallback((r: ExecutiveReport) => {
    const duplicate: ExecutiveReport = { ...r, id: `rpt-${Date.now()}`, title: `${r.title} (copia)` };
    setReports((prev) => [duplicate, ...prev]);
    SwalAlert.success('Duplicado', 'El reporte fue duplicado.');
  }, []);

  const handleDeleteReport = useCallback(async (r: ExecutiveReport) => {
    const result = await SwalAlert.confirm('Eliminar reporte', `¿Eliminar "${r.title}"? Esta acción no se puede deshacer.`);
    if (result.isConfirmed) {
      setReports((prev) => prev.filter((rp) => rp.id !== r.id));
      SwalAlert.success('Eliminado', 'El reporte fue eliminado.');
    }
  }, []);

  const handleSaveReport = useCallback((r: ExecutiveReport) => {
    const existing = reports.find((rp) => rp.id === r.id);
    if (existing) {
      setReports((prev) => prev.map((rp) => rp.id === r.id ? r : rp));
    } else {
      setReports((prev) => [r, ...prev]);
    }
    setShowReportForm(false);
    SwalAlert.success('Guardado', existing ? 'El reporte fue actualizado.' : 'El reporte fue creado.');
  }, [reports]);

  const handleRunReport = useCallback(async (r: ExecutiveReport) => {
    SwalAlert.loading('Ejecutando...', 'Generando reporte.');
    setTimeout(() => {
      SwalAlert.success('Reporte ejecutado', `"${r.title}" fue generado con éxito (simulado).`);
    }, 1200);
  }, []);

  const handleReportCsv = useCallback((r: ExecutiveReport) => {
    const csv = buildReportCsv(r);
    downloadBlob(csv, `reporte-${r.id}-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv;charset=utf-8');
    setReportToast(r.id);
    setTimeout(() => setReportToast(null), 2000);
  }, []);

  const handleReportPdf = useCallback((r: ExecutiveReport) => {
    const w = window.open('', '_blank', 'width=800,height=600');
    if (!w) { setReportToast(r.id); setTimeout(() => setReportToast(null), 2000); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>${r.title}</title><style>body{font-family:system-ui,sans-serif;padding:40px;color:#1a1a1a}h1{font-size:24px;margin-bottom:4px}.period{color:#888;font-size:14px;margin-bottom:32px}table{width:100%;border-collapse:collapse}th{text-align:left;font-size:11px;text-transform:uppercase;color:#888;padding:10px 12px;border-bottom:2px solid #e5e5e5}td{padding:12px;border-bottom:1px solid #eee;font-size:14px}.up{color:#059669}.down{color:#dc2626}.footer{margin-top:40px;font-size:11px;color:#aaa}</style></head><body><h1>${r.title}</h1><p class="period">${r.period}</p><table><thead><tr><th>Métrica</th><th>Valor</th><th>Cambio</th></tr></thead><tbody>${r.metrics.map((m) => `<tr><td>${m.label}</td><td>${m.value}</td><td class="${m.trend}">${m.change}</td></tr>`).join('')}</tbody></table><p class="footer">RadioFrecuenciaWeb · ${new Date().toLocaleDateString('es-ES')}</p></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
    setReportToast(r.id);
    setTimeout(() => setReportToast(null), 2000);
  }, []);

  const handleScheduleReport = useCallback(async (r: ExecutiveReport) => {
    const result = await SwalAlert.confirm('Programar reporte', `¿Programar "${r.title}" para envío automático? (Simulado)`);
    if (result.isConfirmed) {
      SwalAlert.success('Programado', 'El reporte se programó para envío periódico (simulado).');
    }
  }, []);

  const handleViewRadio = useCallback((radio: TopRadio) => setSelectedRadio(radio), []);

  const alertTypeTabs = [
    { id: 'all' as const, label: 'Todas', icon: 'ri-notification-3-line', count: alerts.length },
    { id: 'growing_fast' as const, label: 'Creciendo', icon: 'ri-rocket-2-line', count: alerts.filter((a) => a.type === 'growing_fast').length },
    { id: 'at_risk' as const, label: 'En Riesgo', icon: 'ri-alert-line', count: alerts.filter((a) => a.type === 'at_risk').length },
    { id: 'no_activity' as const, label: 'Sin Actividad', icon: 'ri-pause-circle-line', count: alerts.filter((a) => a.type === 'no_activity').length },
  ];

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
    { key: 'rankings', label: 'Rankings', icon: 'ri-trophy-line' },
    { key: 'growth-alerts', label: 'Growth Alerts', icon: 'ri-line-chart-line' },
    { key: 'reports', label: 'Reports', icon: 'ri-file-chart-line' },
  ];

  /* ─── SHARED LAYOUT WRAPPER ─── */
  const renderLayout = (children: React.ReactNode) => (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Analytics Overview</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Analytics Overview</h2>
                <p className="text-foreground-400 text-xs mt-0.5">Métricas globales de la plataforma · Datos consolidados</p>
              </div>
              <div className="flex items-center gap-2">
                {(activeTab === 'overview' || activeTab === 'rankings') && <TimeRangeSelector active={timeRange} onChange={setTimeRange} />}
              </div>
            </div>
            {/* Tab switcher */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              {tabs.map((tab) => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.key ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'}`}>
                  <i className={`${tab.icon} text-[11px]`}></i>{tab.label}
                </button>
              ))}
            </div>
            {children}
          </div>
        </main>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Analytics Overview</p>
      </div>
    </div>
  );

  /* ─── LOADING ─── */
  if (loadState === 'loading') {
    return renderLayout(
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 animate-pulse">
              <div className="flex items-center justify-between mb-3"><div className="w-20 h-3 bg-background-200 rounded"></div><div className="w-8 h-8 bg-background-200 rounded-lg"></div></div>
              <div className="w-28 h-7 bg-background-200 rounded-lg mb-2"></div>
              <div className="w-16 h-3.5 bg-background-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 animate-pulse">
          <div className="w-40 h-4 bg-background-200 rounded mb-4"></div>
          <div className="w-full h-[160px] bg-background-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  /* ─── ERROR ─── */
  if (loadState === 'error') {
    return renderLayout(
      <div className="flex items-center justify-center py-12">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h2 className="text-foreground-950 font-bold text-base mb-1.5">Error cargando analytics</h2>
          <p className="text-foreground-400 text-xs mb-5">No se pudieron cargar los datos. Verificá tu conexión.</p>
          <button onClick={handleRetry} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-refresh-line text-sm"></i>Reintentar
          </button>
        </div>
      </div>
    );
  }

  /* ─── TAB: OVERVIEW ─── */
  const overviewContent = (
    <div className="space-y-5">
      <AnalyticsFiltersComponent filters={filters} onChange={handleFilterChange} hasActiveFilters={hasActiveFilters} onClear={handleClearFilters} />
      {hasActiveFilters && (
        <div className="flex items-center gap-2 px-3 py-2 bg-accent-50 border border-accent-200 rounded-xl">
          <i className="ri-filter-3-line text-accent-500 text-xs"></i>
          <span className="text-accent-700 text-[10px] font-medium">Filtros activos — los datos están filtrados</span>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-2">
        {filteredKpis.map((kpi) => <KpiCard key={kpi.label} kpi={kpi} />)}
      </div>
      <TimeSeriesChart data={currentTimeSeries} timeRange={timeRange} title={chartTitles[timeRange]} barColor="bg-amber-500" previousBarColor="bg-amber-200/60" />
      <div>
        <h3 className="text-foreground-950 font-bold text-sm mb-3">Métricas por Período</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {additionalChartData.map((chart) => (
            <TimeSeriesChart key={chart.key} data={chart.data} timeRange={timeRange} title={chart.title} barColor={chart.barColor} previousBarColor={chart.previousBarColor} />
          ))}
        </div>
      </div>
      <div className="bg-accent-50 border border-accent-200 rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
              <i className="ri-bar-chart-grouped-line text-accent-500 text-lg"></i>
            </div>
            <div>
              <p className="text-accent-800 text-xs font-semibold">Panel de Analytics Completo</p>
              <p className="text-accent-600/80 text-[10px]">Exportaciones PDF/CSV son simulaciones mock. Usá los tabs para explorar rankings, alertas y reportes.</p>
            </div>
          </div>
          <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1">
            <i className="ri-time-line text-[9px]"></i>Actualizado ahora
          </span>
        </div>
      </div>
    </div>
  );

  /* ─── TAB: RANKINGS ─── */
  const rankingsContent = (
    <div className="space-y-5">
      <AnalyticsFiltersComponent filters={filters} onChange={handleFilterChange} hasActiveFilters={hasActiveFilters} onClear={handleClearFilters} />
      {allRankingsEmpty ? (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4"><i className="ri-search-line text-foreground-300 text-2xl"></i></div>
          <h4 className="text-foreground-800 text-sm font-semibold mb-1.5">Sin resultados</h4>
          <p className="text-foreground-400 text-xs max-w-sm mx-auto">No hay radios que coincidan con los filtros. Probá con otros criterios.</p>
          <button onClick={handleClearFilters} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap">
            <i className="ri-close-line text-xs"></i>Limpiar filtros
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TopRankingList title="Más Escuchadas" data={filteredListeners} valueLabel="Oyentes/mes" onViewRadio={handleViewRadio} />
          <TopRankingList title="Más Activas" data={filteredActive} valueLabel="Índice actividad" onViewRadio={handleViewRadio} accentColor="bg-sky-500" />
          <TopRankingList title="Más Mensajes en Cabina" data={filteredMessages} valueLabel="Mensajes/mes" onViewRadio={handleViewRadio} />
          <TopRankingList title="Mejor Radio Score" data={filteredScore} valueLabel="Score" valueSuffix="/100" onViewRadio={handleViewRadio} accentColor="bg-emerald-500" />
          <TopRankingList title="Stream Caído (Horas)" data={filteredStreamDown} valueLabel="Horas offline" valueSuffix="h" onViewRadio={handleViewRadio} accentColor="bg-red-500" />
        </div>
      )}
    </div>
  );

  /* ─── TAB: GROWTH ALERTS ─── */
  const growthContent = (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {[
          { label: 'Seguidores Nuevos', value: formatNumber(radioGrowthData.reduce((s, r) => s + r.newFollowers, 0)), icon: 'ri-user-add-line', bg: 'bg-sky-50', color: 'text-sky-500' },
          { label: 'Oyentes Nuevos', value: formatNumber(radioGrowthData.reduce((s, r) => s + r.newListeners, 0)), icon: 'ri-headphone-line', bg: 'bg-emerald-50', color: 'text-emerald-500' },
          { label: 'Programas Nuevos', value: String(radioGrowthData.reduce((s, r) => s + r.newPrograms, 0)), icon: 'ri-calendar-check-line', bg: 'bg-violet-50', color: 'text-violet-500' },
          { label: 'Locutores Nuevos', value: String(radioGrowthData.reduce((s, r) => s + r.newHosts, 0)), icon: 'ri-mic-line', bg: 'bg-amber-50', color: 'text-amber-500' },
          { label: 'Sponsors Nuevos', value: String(radioGrowthData.reduce((s, r) => s + r.newSponsors, 0)), icon: 'ri-hand-heart-line', bg: 'bg-rose-50', color: 'text-rose-500' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
            <div className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center shrink-0`}><i className={`${kpi.icon} ${kpi.color} text-lg`}></i></div>
            <div className="min-w-0"><p className="text-foreground-400 text-[10px] font-medium truncate">{kpi.label}</p><p className="text-foreground-800 text-lg font-bold leading-tight">{kpi.value}</p></div>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-notification-3-line text-foreground-400 text-sm"></i>
          <h3 className="text-foreground-800 text-sm font-bold">Alertas de Crecimiento</h3>
          <span className="text-foreground-400 text-[10px]">{alerts.length} alertas</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          {alertTypeTabs.map((tab) => {
            const isActive = alertFilter === tab.id;
            const activeCls = tab.id === 'all' ? 'bg-foreground-950 text-background-50' : tab.id === 'growing_fast' ? 'bg-emerald-100 text-emerald-700' : tab.id === 'at_risk' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
            return (
              <button key={tab.id} onClick={() => setAlertFilter(tab.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${isActive ? activeCls : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-100'}`}>
                <i className={`${tab.icon} text-[10px]`}></i>{tab.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold ${isActive ? (tab.id === 'all' ? 'bg-background-50/20 text-background-50' : 'bg-white/30') : 'bg-background-200 text-foreground-500'}`}>{tab.count}</span>
              </button>
            );
          })}
        </div>
        {filteredAlerts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredAlerts.map((alert) => <GrowthAlertCard key={alert.id} alert={alert} onResolve={handleResolveAlert} onNote={handleNoteAlert} />)}
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-check-double-line text-foreground-300 text-lg"></i></div>
            <p className="text-foreground-500 text-xs font-medium">Sin alertas en esta categoría</p>
          </div>
        )}
      </div>
    </div>
  );

  /* ─── TAB: REPORTS ─── */
  const reportsContent = (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground-800 text-sm font-bold">Reportes Configurables</h3>
          <p className="text-foreground-400 text-[10px]">{reports.length} reportes · Creá, editá, duplicá y exportá</p>
        </div>
        <button onClick={handleCreateReport} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap">
          <i className="ri-add-line text-sm"></i>Crear Reporte
        </button>
      </div>
      {reports.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {reports.map((report) => (
            <ReportCard key={report.id} report={report} onEdit={handleEditReport} onDuplicate={handleDuplicateReport} onDelete={handleDeleteReport} onRun={handleRunReport} onCsv={handleReportCsv} onPdf={handleReportPdf} />
          ))}
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-file-chart-line text-foreground-300 text-xl"></i></div>
          <p className="text-foreground-500 text-sm font-medium">Sin reportes</p>
          <p className="text-foreground-300 text-xs mt-1">Creá tu primer reporte para empezar</p>
        </div>
      )}
    </div>
  );

  const tabContent = activeTab === 'overview' ? overviewContent : activeTab === 'rankings' ? rankingsContent : activeTab === 'growth-alerts' ? growthContent : reportsContent;

  return (
    <>
      {renderLayout(tabContent)}
      {selectedRadio && <RadioDetailModal radio={selectedRadio} onClose={() => setSelectedRadio(null)} />}
      {showReportForm && <ReportFormModal report={reportForm} onClose={() => setShowReportForm(false)} onSave={handleSaveReport} />}
    </>
  );
}